// A small country flag, for anywhere the app names a country by its code.
// The flags are the SVGs of the flag-icons package, drawn by its CSS
// classes: each flag is fetched only when one is shown.
import "flag-icons/css/flag-icons.min.css";

import { Box, SxProps, Theme } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";

// Codes the sources use that ISO names otherwise
const ALIASES: Record<string, string> = { uk: "gb", el: "gr" };

// Names in English, made once: making one is not free
let regionNames: Intl.DisplayNames | null | undefined;

// A country's name in English from its ISO code, or null for a code the
// browser does not know
export const countryName = (code: string): string | null => {
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      regionNames = null;
    }
  }
  try {
    const name = regionNames?.of(code.toUpperCase());
    return name && name.toUpperCase() !== code.toUpperCase() ? name : null;
  } catch {
    return null;
  }
};

// The flag-icons code of a country code, or null where there is no flag:
// flag-icons has one for every two-letter ISO code
export const flagCode = (code: string): string | null => {
  const lower = code.trim().toLowerCase();
  const found = ALIASES[lower] ?? lower;
  return /^[a-z]{2}$/.test(found) ? found : null;
};

interface CountryFlagProps {
  // An ISO 3166-1 alpha-2 code, as the data carries it; nothing is drawn
  // for none
  code: string | null | undefined;
  // The flag's height; its width is four thirds of it. By default the
  // height of a line of the text around it.
  size?: number | string;
  // A circle cut from the flag's square version, as wide as it is high
  round?: boolean;
  sx?: SxProps<Theme>;
}

// A country's flag, its name told on hover and to screen readers; the code
// itself in small capitals where there is no flag for it
export const CountryFlag: React.FC<CountryFlagProps> = ({
  code,
  size = "1em",
  round = false,
  sx,
}) => {
  if (!code) {
    return null;
  }
  const flag = flagCode(code);
  const name = countryName(ALIASES[code.toLowerCase()] ?? code) ?? code.toUpperCase();
  const extra = Array.isArray(sx) ? sx : [sx];
  if (!flag) {
    return (
      <Box
        component="span"
        title={name}
        sx={[{ ...monospaceStyle, fontSize: "0.85em", color: "text.secondary" }, ...extra]}
      >
        {code}
      </Box>
    );
  }
  const height = typeof size === "number" ? `${size}px` : size;
  return (
    <Box
      component="span"
      role="img"
      aria-label={name}
      title={name}
      className={round ? `fi fis fi-${flag}` : `fi fi-${flag}`}
      // Sized inline: flag-icons' own width rules outrank a single class
      style={{ height, width: round ? height : `calc(${height} * 4 / 3)` }}
      sx={[
        {
          flexShrink: 0,
          lineHeight: 1,
          borderRadius: round ? "50%" : "2px",
          backgroundSize: "cover",
          // A hairline round it, so that a white flag shows on a light
          // surface and a dark flag on a dark one
          boxShadow: (theme: Theme) => `0 0 0 1px ${theme.palette.divider}`,
          verticalAlign: "-0.125em",
        },
        ...extra,
      ]}
    />
  );
};

export default CountryFlag;
